import type { ComponentType, SVGProps } from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import EditIcon from '../../../asset/icons/photo-viewer/edit.svg?react';
import DownloadIcon from '../../../asset/icons/photo-viewer/download.svg?react';
import RequestSupportIcon from '../../../asset/icons/photo-viewer/request-support.svg?react';
import CloseIcon from '../../../asset/icons/photo-viewer/close.svg?react';
import EditIconDisabled from '../../../asset/icons/photo-viewer/edit-disabled.svg?react';
import DownloadIconDisabled from '../../../asset/icons/photo-viewer/download-disabled.svg?react';
import RequestSupportDisabledIcon from '../../../asset/icons/photo-viewer/request-support-disabled.svg?react';
import CloseIconDisabled from '../../../asset/icons/photo-viewer/close-disabled.svg?react';

type SvgIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

interface AssetViewToolbarProps {
  photoName: string;
  isDisabled: boolean;
  // TODO(DV-2252): staged items are optimistic-only (fake ids) — re-enable
  // once the commit endpoint persists them as real Assets.
  hideRequestSupport?: boolean;
  onRequestSupport: () => void;
  onToggleServices: () => void;
  onDownload: () => void;
  onClose: () => void;
}

export default function AssetViewToolbar({
  photoName,
  isDisabled,
  hideRequestSupport = false,
  onRequestSupport,
  onToggleServices,
  onDownload,
  onClose
}: AssetViewToolbarProps) {
  const getIcon = (active: SvgIconComponent, disabled: SvgIconComponent) =>
    isDisabled ? disabled : active;

  return (
    <div
      className={cn(
        'w-full absolute z-30',
        'flex justify-between items-center',
        'px-3 py-2'
      )}
    >
      <span className="text-white text-lg font-semibold">{photoName}</span>

      <div className="flex gap-3 items-center">
        <TooltipProvider>
          {!hideRequestSupport && (
            <ToolbarIconButton
              tooltip="Request support"
              tooltipSide="left"
              disabled={isDisabled}
              onClick={onRequestSupport}
              icon={getIcon(RequestSupportIcon, RequestSupportDisabledIcon)}
            />
          )}
          <ToolbarIconButton
            tooltip="Image Edits & Upgrades"
            tooltipSide="bottom"
            disabled={isDisabled}
            onClick={onToggleServices}
            icon={getIcon(EditIcon, EditIconDisabled)}
          />
          <ToolbarIconButton
            tooltip="Download"
            tooltipSide="bottom"
            disabled={isDisabled}
            onClick={onDownload}
            icon={getIcon(DownloadIcon, DownloadIconDisabled)}
          />
          <ToolbarIconButton
            tooltip="Close"
            tooltipSide="bottom"
            disabled={isDisabled}
            onClick={onClose}
            icon={getIcon(CloseIcon, CloseIconDisabled)}
          />
        </TooltipProvider>
      </div>
    </div>
  );
}

interface ToolbarIconButtonProps {
  tooltip: string;
  tooltipSide: 'top' | 'bottom' | 'left' | 'right';
  disabled: boolean;
  onClick: () => void;
  icon: SvgIconComponent;
}

function ToolbarIconButton({
  tooltip,
  tooltipSide,
  disabled,
  onClick,
  icon: IconComp
}: ToolbarIconButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className={cn(
            'p-3 rounded-full text-white bg-white/[0.08] cursor-pointer',
            'transition-colors duration-200',
            'hover:bg-white/20',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          aria-label={tooltip}
          onClick={onClick}
          disabled={disabled}
        >
          <IconComp style={{ fontSize: 28 }} className="w-7 h-7" />
        </button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}
